'use client';

import { debounce } from '@/lib/debounce';
import { isActionError } from '@/lib/error';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'nextjs-toploader/app';
import { useState } from 'react';
import { useIsMobile } from '../use-mobile';

/** FIXME: Update with proper type file */
interface UsePaymentMutationsProps<V> {
  verifyFn: (value: string) => Promise<any>;
  addPaymentFn: (data: V) => Promise<any>;
  type: 'upi' | 'bank';
  onSuccessClose?: (status: boolean) => void; // Optional callback to close modal
}
export function usePaymentMutations<V>({
  verifyFn,
  addPaymentFn,
  type,
  onSuccessClose
}: UsePaymentMutationsProps<V>) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [name, setName] = useState<string | null>(null);
  const [verificationErrors, setVerificationErrors] = useState('');
  const [paymentErrors, setPaymentErrors] = useState('');
  const MINLENGTH = type === 'bank' ? 11 : 6;
  const verifyMutation = useMutation({
    mutationFn: async (value: string) => {
      const response = await verifyFn(value);
      if (isActionError(response)) {
        setVerificationErrors(response.error);
        throw response.error;
      }
      return response;
    },
    onSuccess: data => {
      if (data?.success) {
        setIsValid(true);
        setName(data?.bankDetails ? `${data?.bankDetails?.bank}, ${data?.bankDetails.branch}` : data?.customerName);
      }
      setIsValidating(false);
    },
    onError: () => {
      setIsValid(false);
      setIsValidating(false);
      setName(null);
    }
  });
  const addPaymentMutation = useMutation({
    mutationFn: async (value: V) => {
      const response = await addPaymentFn(value);
      if (isActionError(response)) {
        setPaymentErrors(response?.error);
        throw response.error;
      }
      return response;
    },
    onSuccess: () => {
      setIsSuccess(true);

      // Redirect only on mobile
      setTimeout(async () => {
        if (isMobile) {
          router.replace('/profile/basic-details');
        } else {
          onSuccessClose?.(false);
        }
        await queryClient.invalidateQueries({
          queryKey: ['paymentMethods']
        });
      }, 3000);
    },
    onError: () => {
      setIsValid(false);
      setIsValidating(false);
      setName(null);
    }
  });
  const validate = debounce((value: string) => {
    setIsValid(false);
    setName(null);
    if (value.length >= MINLENGTH) {
      setIsValidating(true);
      verifyMutation.mutate(value);
    }
  }, 1000);
  const addPayment = (data: V) => {
    addPaymentMutation.mutate(data);
  };
  return {
    validate,
    addPayment,
    isValidating,
    isValid,
    isSuccess,
    verifyError: verificationErrors,
    addPaymentError: paymentErrors,
    name
  };
}